import React, { useMemo } from 'react';
import { calculateFinancials } from '../../services/financialCalculations';
import { formatCurrency } from '../../utils/formatters';

const LandCostSensitivityTable = ({ inputs }) => {
    const { sensitivityData, tariffRange, landCostRange } = useMemo(() => {
        const baseTariff = inputs.onGridTariff;
        const baseLandCost = inputs.landCostPerHectare;

        const tariffRange = [-0.2, -0.1, 0, 0.1, 0.2].map(mod => baseTariff * (1 + mod));
        const landCostRange = [-0.5, -0.25, 0, 0.25, 0.5].map(mod => baseLandCost * (1 + mod));

        const sensitivityData = landCostRange.map(landCost => {
            return tariffRange.map(tariff => {
                const tempInputs = { ...inputs, onGridTariff: tariff, landCostPerHectare: landCost };
                return calculateFinancials(tempInputs).indicators.projectNPV;
            });
        });

        return { sensitivityData, tariffRange, landCostRange };
    }, [inputs]);

    return (
        <div className="mt-6">
            <h4 className="text-md font-semibold text-gray-800 dark:text-gray-100 mb-2">NPV Sensitivity Analysis (Tariff vs. Land Cost)</h4>
            <div className="overflow-x-auto">
                <table className="min-w-full text-sm text-center border-collapse border border-gray-300 dark:border-gray-600">
                    <thead className="bg-gray-100 dark:bg-gray-700">
                        <tr>
                            <th className="p-2 border border-gray-300 dark:border-gray-600">
                                <div className="flex flex-col items-center">
                                    <span className="font-bold">Land Cost ↓</span>
                                    <span className="font-normal text-xs">Tariff →</span>
                                </div>
                            </th>
                            {tariffRange.map((tariff, index) => (
                                <th key={index} className="p-2 border border-gray-300 dark:border-gray-600 font-medium">{formatCurrency(tariff, 3)}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {landCostRange.map((landCost, rowIndex) => (
                            <tr key={rowIndex} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                                <th className="p-2 border border-gray-300 dark:border-gray-600 font-medium bg-gray-100 dark:bg-gray-700">{formatCurrency(landCost)}</th>
                                {sensitivityData[rowIndex].map((npv, colIndex) => {
                                    const isBaseCase = rowIndex === 2 && colIndex === 2;
                                    const isNegative = npv < 0;
                                    return (
                                        <td key={colIndex} className={`p-2 border border-gray-300 dark:border-gray-600 ${isBaseCase ? 'bg-blue-100 dark:bg-blue-900/50 font-bold' : ''} ${isNegative ? 'text-red-500' : ''}`}>
                                            {formatCurrency(npv)}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default LandCostSensitivityTable; 